import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Button,
    Table,
    Checkbox,
    Text,
    Header,
    Loader,
    Divider,
    Flex,
    ArrowLeftIcon,
    Avatar,
    ChevronEndIcon,
    ChevronStartIcon,
    Input,
    SearchIcon,
    Alert,
    Dialog
} from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { useHistory } from "react-router-dom";
import { useSSOAuth } from "./SSOAuthService";
import PresenceSubscriptionCard, { getApiBaseUrl, parseJwt } from "./PresenceSubscriptionCard";
import MappingSelector from "./MappingSelector";
import PresenceBadge from "./PresenceBadge";
import ActionIcons, { TooltipWrapper } from "./ActionIcons";
import IconLegend from "./IconLegend";

// Prevents proxies (like devtunnels) from throwing 503 on special characters
function safeJsonStringify(obj) {
    return JSON.stringify(obj).replace(/[\u007F-\uFFFF]/g, function(chr) {
        return "\\u" + ("0000" + chr.charCodeAt(0).toString(16)).slice(-4);
    });
}

/**
 * StatusToggle Component (Smooth UI without locking)
 */
const StatusToggle = ({ user, contactId, currentStatus, onToggleChange }) => {
    const isMappingSelected = Boolean(contactId && contactId !== "");
    const [showMappingAlert, setShowMappingAlert] = useState(false);

    const handleToggle = (e, { checked }) => {
        if (checked && !isMappingSelected) {
            setShowMappingAlert(true);
            return;
        }
        onToggleChange(user, checked ? "Active" : "Inactive", contactId);
    };

    return (
        <>
            <Checkbox
                toggle
                checked={currentStatus === "Active"}
                label={currentStatus === "Active" ? "Aktiv" : "Inaktiv"}
                onChange={handleToggle}
            />
            <Dialog
                open={showMappingAlert}
                onConfirm={() => setShowMappingAlert(false)}
                confirmButton="OK"
                header="Hinweis"
                content="Bitte wählen Sie zuerst einen Zuordnungsbenutzer aus."
                onCancel={() => setShowMappingAlert(false)}
            />
        </>
    );
};

const ITEMS_PER_PAGE = 20;
const GRAPH_TOP = 100;

const SYNC_FILTER_OPTIONS = [
    { key: 'all', header: 'Alle' },
    { key: 'synced', header: 'Synchronisiert' },
    { key: 'notSynced', header: 'Nicht synchronisiert' },
];

const MAPPING_FILTER_OPTIONS = [
    { key: 'all', header: 'Alle' },
    { key: 'mapped', header: 'Zugeordnet' },
    { key: 'unmapped', header: 'Nicht zugeordnet' },
];

const UsersConfig = () => {
    const history = useHistory();
    const { getToken, loading: authLoading } = useSSOAuth();


    // --- Data State ---
    const [users, setUsers] = useState([]);
    const [totalUserCount, setTotalUserCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Search State ---
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    // --- Graph Pagination State ---
    // Exclude disabled/terminated accounts (accountEnabled=false) AND
    // non-person accounts (shared mailboxes, service accounts, rooms).
    // Mirrors backend fetchAndUpsertGraphUsers filter.
    const PERSON_FILTER = encodeURIComponent("accountEnabled eq true and givenName ne null and surname ne null");
    const [currentLink, setCurrentLink] = useState(`https://graph.microsoft.com/v1.0/users?$top=${GRAPH_TOP}&$count=true&$filter=${PERSON_FILTER}&$orderby=displayName&$select=id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,department,companyName,officeLocation,businessPhones,mobilePhone,accountEnabled,employeeId,city,country,preferredLanguage`);
    const [nextLink, setNextLink] = useState(null);


    // --- Config State ---
    const [currentToken, setCurrentToken] = useState(null);
    const [userMappings, setUserMappings] = useState({});
    const [userStatuses, setUserStatuses] = useState({});
    const [dropdownOptions, setDropdownOptions] = useState([]);
    const [configLoading, setConfigLoading] = useState(false);

    // --- Presence Data (from TSCM DB via webhook) ---
    const [presenceData, setPresenceData] = useState({});

    // --- Profile Photos ---
    const [userPhotos, setUserPhotos] = useState({});
    const photoUrlsRef = useRef({});

    // --- Client-side Filters ---
    const [syncFilter, setSyncFilter] = useState('all');
    const [mappingFilter, setMappingFilter] = useState('all');
    const [clientPage, setClientPage] = useState(0);

    // --- Auto-Map State ---
    const [autoMapLoading, setAutoMapLoading] = useState(false);
    const [fullSyncLoading, setFullSyncLoading] = useState(false);

    // --- BATCH BUFFER for presenceStatusConfig ---
    const batchBufferRef = useRef(new Map());
    const batchTimerRef = useRef(null);
    const batchAbortRef = useRef(null);
    const batchInflightRef = useRef(new Map());

    // --- Config fetch abort ---
    const configAbortRef = useRef(null);

    // --- Create Employee Refs (Debounce & Abort) ---
    const createTimeoutRef = useRef(null);
    const createAbortControllerRef = useRef(null);

    // --- Toast feedback ---
    const [toast, setToast] = useState({ visible: false, type: 'success', message: '' });
    const toastTimerRef = useRef(null);

    // --- Modal State ---
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedUserForCreate, setSelectedUserForCreate] = useState(null);

    // --- Presence subscription refresh trigger ---
    const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);
    const autoRenewTimerRef = useRef(null);

    // --- Auto-Map Confirm Dialog State ---
    const [autoMapConfirm, setAutoMapConfirm] = useState({ open: false, matchBy: null });
    const [fullSyncConfirm, setFullSyncConfirm] = useState(false);

    // --- Polling interval ref ---
    const pollingRef = useRef(null);

    // --- Cleanup on unmount ---
    useEffect(() => {
        return () => {
            if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
            if (batchAbortRef.current) batchAbortRef.current.abort();
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            if (createTimeoutRef.current) clearTimeout(createTimeoutRef.current);
            if (createAbortControllerRef.current) createAbortControllerRef.current.abort();
            if (pollingRef.current) clearInterval(pollingRef.current);
            Object.values(photoUrlsRef.current).forEach(URL.revokeObjectURL);
        };
    }, []);

    // --- Teams SDK loading notification ---
    useEffect(() => {
        app.initialize().then(() => {
            app.notifyAppLoaded();
        });
    }, []);

    // --- Debounce main search ---
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 600);
        return () => clearTimeout(timerId);
    }, [searchTerm]);

    useEffect(() => {
        let url = `https://graph.microsoft.com/v1.0/users?$top=${GRAPH_TOP}&$count=true&$filter=${PERSON_FILTER}&$orderby=displayName&$select=id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,department,companyName,officeLocation,businessPhones,mobilePhone,accountEnabled,employeeId,city,country,preferredLanguage`;
        if (debouncedSearchTerm.trim() !== "") {
            const cleanTerm = debouncedSearchTerm.replace(/[":\\()]/g, '').replace(/\b(AND|OR|NOT)\b/gi, '');
            url += `&$search="displayName:${cleanTerm}" OR "userPrincipalName:${cleanTerm}" OR "mail:${cleanTerm}"`;
        }
        setCurrentLink(url);
        setClientPage(0);
    }, [debouncedSearchTerm]);

    // Fetch Tenant Users from Graph
    useEffect(() => {
        const fetchUsers = async () => {
            if (!currentToken) return;
            try {
                setLoading(true);
                setError(null);
                const response = await fetch(currentLink, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${currentToken}`,
                        'ConsistencyLevel': 'eventual'
                    }
                });
                if (!response.ok) throw new Error(`Failed to fetch users: ${response.statusText}`);
                const data = await response.json();
                if (data['@odata.count']) setTotalUserCount(data['@odata.count']);
                setNextLink(data['@odata.nextLink'] || null);
                setUsers(data.value || []);
                setClientPage(0);
            } catch (err) {
                console.error("Failed to load users:", err);
                setError(err.message);
            } finally {
                setLoading(false);
                app.notifySuccess();
            }
        };
        fetchUsers();
    }, [currentToken, currentLink]);

    // Init Token
    useEffect(() => {
        const initToken = async () => {
            try {
                const token = await getToken(['User.Read.All', 'Presence.Read.All', "Calendars.Read", 'offline_access']);
                setCurrentToken(token);
            } catch (e) { setError("Authentifizierung fehlgeschlagen."); }
        };
        initToken();
    }, [getToken]);

    const loadConfigData = useCallback(async () => {
        if (!currentToken) return null;
        if (configAbortRef.current) configAbortRef.current.abort();
        const abortController = new AbortController();
        configAbortRef.current = abortController;
        setConfigLoading(true);
        try {
            const decoded = parseJwt(currentToken);
            const adminAadId = decoded ? (decoded.oid || decoded.sub) : null;
            const tenantId = decoded ? decoded.tid : null;

            if (!adminAadId || !tenantId) throw new Error("Cannot extract Admin ID or Tenant ID from token");

            const baseUrl = getApiBaseUrl();
            const url = `${baseUrl}/MSTeamsUser/presenceStatusConfig/${tenantId}/${adminAadId}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                },
                signal: abortController.signal,
            });

            if (response.ok) {
                const dbData = await response.json();
                let rawDataArray = [];
                if (dbData.data) {
                    if (Array.isArray(dbData.data.employeeList)) rawDataArray = dbData.data.employeeList;
                    else if (Array.isArray(dbData.data.userAccounts)) rawDataArray = dbData.data.userAccounts;
                    else if (Array.isArray(dbData.data.data)) rawDataArray = dbData.data.data;
                    else if (Array.isArray(dbData.data)) rawDataArray = dbData.data;
                } else if (Array.isArray(dbData)) {
                    rawDataArray = dbData;
                }

                const optionsMap = new Map();
                rawDataArray.forEach(acc => {
                    const name = (acc.first_name || acc.last_name)
                        ? `${acc.first_name || ''} ${acc.last_name || ''}`.trim()
                        : (acc.name || acc.displayName || acc.display_name || "Unknown User");

                    const idValue = acc.contact_id || acc.contactId || acc.id || acc.user_account_id;

                    if (idValue) {
                        optionsMap.set(String(idValue), {
                            header: name,
                            data: {
                                name: name,
                                id: String(idValue),
                                email: acc.email || acc.user_principal_name || "No Email",
                                customer_id: acc.customer_id ? String(acc.customer_id) : null,
                                phone_service_id: acc.phone_service_id ? String(acc.phone_service_id) : null
                            }
                        });
                    }
                });
                const options = Array.from(optionsMap.values());
                setDropdownOptions(options.length > 0 ? options : [{ header: "No Data Found", data: null }]);

                const newMappings = {};
                const newStatuses = {};
                const newPresence = {};

                const extractMappings = (list) => {
                    if (!Array.isArray(list)) return;
                    list.forEach(item => {
                        const aadIdVal = item.aad_id || item.aadId || item.AadId;
                        if (aadIdVal) {
                            const aadIdLower = String(aadIdVal).toLowerCase();
                            const contactIdVal = item.contact_id || item.contactId || item.id;
                            if (contactIdVal) {
                                newMappings[aadIdLower] = String(contactIdVal);
                            }
                            const statusVal = item.sync_status || item.syncStatus;
                            if (statusVal) {
                                newStatuses[aadIdLower] = statusVal;
                            }
                            // Extract presence data from extended response
                            if (item.availability || item.activity) {
                                newPresence[aadIdLower] = {
                                    availability: item.availability || null,
                                    activity: item.activity || null,
                                    presence_updated_at: item.presence_updated_at || null,
                                };
                            }
                        }
                    });
                };

                if (dbData.data) {
                    extractMappings(dbData.data.userStatusList);
                    extractMappings(dbData.data.presenceUsers);
                    extractMappings(dbData.data.userMappings);
                    extractMappings(dbData.data.employeeList);
                    extractMappings(dbData.data.userAccounts);
                    if (Array.isArray(dbData.data)) extractMappings(dbData.data);
                }
                if (Array.isArray(dbData)) extractMappings(dbData);

                setUserMappings(newMappings);
                setUserStatuses(newStatuses);
                setPresenceData(newPresence);

                return options;
            }
            return null;
        } catch (e) {
            console.error("Error loading tenant data:", e);
            return null;
        } finally {
            setConfigLoading(false);
        }
    }, [currentToken]);

    // Initial load of config data
    useEffect(() => {
        loadConfigData();
    }, [loadConfigData]);

    // Polling: refresh config data every 45s for presence updates
    useEffect(() => {
        if (!currentToken) return;
        pollingRef.current = setInterval(() => {
            loadConfigData();
        }, 45000);
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
            if (configAbortRef.current) configAbortRef.current.abort();
        };
    }, [currentToken, loadConfigData]);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ visible: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast(prev => ({ ...prev, visible: false }));
        }, 4000);
    }, []);

    const flushBuffer = useCallback(async () => {
        if (batchBufferRef.current.size === 0 || !currentToken) return;

        const entries = Array.from(batchBufferRef.current.values());
        batchBufferRef.current.clear();

        entries.forEach(e => batchInflightRef.current.set(String(e.aad_id).toLowerCase(), e));

        const abortController = new AbortController();
        batchAbortRef.current = abortController;

        const removeInflight = () => {
            entries.forEach(e => {
                const key = String(e.aad_id).toLowerCase();
                if (batchInflightRef.current.get(key) === e) {
                    batchInflightRef.current.delete(key);
                }
            });
        };

        try {
            const decoded = parseJwt(currentToken);
            const tenantId = decoded ? decoded.tid : "";
            const loggedInAadId = decoded ? (decoded.oid || decoded.sub) : "";

            const baseUrl = getApiBaseUrl();
            const url = `${baseUrl}/MSTeamsUser/presenceStatusConfig`;

            const payload = { aad_id: loggedInAadId, tenant_id: tenantId, data: entries };

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json; charset=utf-8',
                    'Authorization': `Bearer ${currentToken}`
                },
                body: safeJsonStringify(payload),
                signal: abortController.signal
            });

            const resultData = await response.json();
            if (!response.ok || resultData.status === "error" || resultData.error) {
                throw new Error(resultData.message || `Save failed: ${response.status}`);
            }

            removeInflight();
            console.log(`Batch saved ${entries.length} config(s)`);
            showToast('success', `${entries.length} Änderung${entries.length > 1 ? 'en' : ''} gespeichert`);
        } catch (e) {
            removeInflight();
            if (e.name !== 'AbortError') {
                console.error("Batch save failed:", e);
                showToast('error', 'Speichern fehlgeschlagen. Bitte laden Sie die Seite neu.');
            }
        } finally {
            if (batchAbortRef.current === abortController) {
                batchAbortRef.current = null;
            }
        }
    }, [currentToken, showToast]);

    const handleSaveUserConfig = useCallback((user, newContactId, newStatus, specificAccountDetail = null) => {
        const aadIdLower = String(user.id).toLowerCase();

        setUserMappings(prev => ({ ...prev, [aadIdLower]: newContactId ? String(newContactId) : null }));
        setUserStatuses(prev => ({ ...prev, [aadIdLower]: newStatus }));

        const accountDetail = specificAccountDetail ||
            dropdownOptions.find(opt => opt.data && String(opt.data.id) === String(newContactId))?.data || {};

        const payloadData = {
            aad_id: user.id,
            display_name: user.displayName || "",
            given_name: user.givenName || "",
            surname: user.surname || "",
            email: user.userPrincipalName || user.mail || "",
            job_title: user.jobTitle || "",
            department: user.department || "",
            company_name: user.companyName || "",
            office_location: user.officeLocation || "",
            business_phones: user.businessPhones || [],
            mobile_phone: user.mobilePhone || "",
            account_enabled: user.accountEnabled ?? null,
            employee_id: user.employeeId || "",
            city: user.city || "",
            country: user.country || "",
            preferred_language: user.preferredLanguage || "",
            user_principal_name: user.userPrincipalName || "",
            contact_id: newContactId ? String(newContactId) : null,
            customer_id: accountDetail.customer_id ? String(accountDetail.customer_id) : null,
            phone_service_id: accountDetail.phone_service_id ? String(accountDetail.phone_service_id) : null,
            sync_status: newStatus
        };

        batchBufferRef.current.set(aadIdLower, payloadData);

        if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
        batchTimerRef.current = setTimeout(() => { flushBuffer(); }, 3000);
    }, [dropdownOptions, flushBuffer]);

    // --- Handle Create Employee ---
    const handleConfirmCreateEmployee = () => {
        if (!currentToken || !selectedUserForCreate) return;

        const userToCreate = selectedUserForCreate;
        setCreateModalOpen(false);

        if (createTimeoutRef.current) clearTimeout(createTimeoutRef.current);
        if (createAbortControllerRef.current) createAbortControllerRef.current.abort();

        createTimeoutRef.current = setTimeout(async () => {
            const abortController = new AbortController();
            createAbortControllerRef.current = abortController;

            try {
                const decoded = parseJwt(currentToken);
                const loggedInAadId = decoded ? (decoded.oid || decoded.sub) : "";

                const baseUrl = getApiBaseUrl();
                const url = `${baseUrl}/MSTeamsUser/createEmployee`;

                const payload = {
                    aad_id: loggedInAadId,
                    employee_aad_id: userToCreate.id || "",
                    display_name: userToCreate.displayName || "",
                    given_name: userToCreate.givenName || "",
                    surname: userToCreate.surname || "",
                    email: userToCreate.mail || "",
                    user_principal_name: userToCreate.userPrincipalName || "",
                    job_title: userToCreate.jobTitle || "",
                    department: userToCreate.department || "",
                    company_name: userToCreate.companyName || "",
                    office_location: userToCreate.officeLocation || "",
                    business_phones: userToCreate.businessPhones || [],
                    mobile_phone: userToCreate.mobilePhone || "",
                    account_enabled: userToCreate.accountEnabled ?? null,
                    employee_id: userToCreate.employeeId || "",
                    city: userToCreate.city || "",
                    country: userToCreate.country || "",
                    preferred_language: userToCreate.preferredLanguage || ""
                };

                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json; charset=utf-8',
                        'Authorization': `Bearer ${currentToken}`
                    },
                    body: safeJsonStringify(payload),
                    signal: abortController.signal
                });

                const resultData = await response.json();
                if (!response.ok || resultData.status === "error" || resultData.error) {
                    throw new Error(resultData.message || `Create failed: ${response.status}`);
                }

                const latestOptions = await loadConfigData();

                const pendingEntries = new Map([...batchInflightRef.current, ...batchBufferRef.current]);
                pendingEntries.forEach((entry, aadIdLower) => {
                    setUserMappings(prev => ({ ...prev, [aadIdLower]: entry.contact_id || null }));
                    setUserStatuses(prev => ({ ...prev, [aadIdLower]: entry.sync_status }));
                });

                let didAutoMap = false;
                if (latestOptions) {
                    const targetEmail = (userToCreate.userPrincipalName || userToCreate.mail || "").toLowerCase();
                    const createdEmployee = latestOptions.find(opt => opt.data && opt.data.email && opt.data.email.toLowerCase() === targetEmail);

                    if (createdEmployee) {
                        didAutoMap = true;
                        handleSaveUserConfig(userToCreate, createdEmployee.data.id, "Inactive", createdEmployee.data);
                    }
                }

                if (!didAutoMap) {
                    showToast('success', 'Mitarbeiter angelegt');
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log(`Create API aborted for: ${userToCreate?.displayName}`);
                } else {
                    console.error("Failed to create employee:", error);
                    showToast('error', 'Mitarbeiter konnte nicht angelegt werden');
                }
            } finally {
                if (createAbortControllerRef.current === abortController) {
                    createAbortControllerRef.current = null;
                }
            }
        }, 800);
    };

    const handleCancelCreate = () => {
        if (createTimeoutRef.current) clearTimeout(createTimeoutRef.current);
        if (createAbortControllerRef.current) createAbortControllerRef.current.abort();
        setCreateModalOpen(false);
    };

    // --- Bulk Auto-Map ---
    const handleAutoMap = async (matchBy) => {
        if (!currentToken) return;
        setAutoMapLoading(true);
        try {
            const decoded = parseJwt(currentToken);
            const tenantId = decoded ? decoded.tid : null;
            const aadId = decoded ? (decoded.oid || decoded.sub) : null;
            const baseUrl = getApiBaseUrl();

            const response = await fetch(`${baseUrl}/MSTeamsUser/autoMap`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                },
                body: JSON.stringify({ tenant_id: tenantId, aad_id: aadId, matchBy })
            });

            const data = await response.json();
            if (response.ok && !data.error) {
                showToast('success', `Auto-Map: ${data.newMappings || 0} neue Zuordnungen erstellt`);
                await loadConfigData();
            } else {
                showToast('error', data.message || 'Auto-Map fehlgeschlagen');
            }
        } catch (e) {
            console.error("Auto-map failed:", e);
            showToast('error', 'Auto-Map fehlgeschlagen');
        } finally {
            setAutoMapLoading(false);
        }
    };

    // --- Re-run Full Sync ---
    const handleFullSync = async () => {
        if (!currentToken) return;
        setFullSyncLoading(true);
        try {
            const decoded = parseJwt(currentToken);
            const tenantId = decoded ? decoded.tid : null;
            const aadId = decoded ? (decoded.oid || decoded.sub) : null;
            const baseUrl = getApiBaseUrl();

            const response = await fetch(`${baseUrl}/MSTeamsUser/triggerFullSync`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                },
                body: JSON.stringify({ tenant_id: tenantId, aad_id: aadId })
            });

            const data = await response.json();
            if (response.ok && !data.error) {
                showToast('success', 'Vollständige Synchronisierung gestartet');
                setTimeout(() => loadConfigData(), 3000);
            } else {
                showToast('error', data.message || 'Synchronisierung fehlgeschlagen');
            }
        } catch (e) {
            console.error("Full sync failed:", e);
            showToast('error', 'Synchronisierung fehlgeschlagen');
        } finally {
            setFullSyncLoading(false);
        }
    };

    // --- Navigation ---
    const handleBack = () => history.push("/setting");
    const handleSettings = () => history.push("/sync/settings");

    // --- Graph pagination (load more from server) ---
    const handleNextPage = () => { if (nextLink) { setCurrentLink(nextLink); }};

    const handleMappingSelect = (user, newContactId) => {
        const aadIdLower = String(user.id).toLowerCase();
        const currentStatus = newContactId ? (userStatuses[aadIdLower] || "Inactive") : "Inactive";
        handleSaveUserConfig(user, newContactId, currentStatus);
    };

    // Auto-renew presence subscription when the first employee is activated.
    // Called after batch flush (~3s) so the backend already has the updated config.
    const autoRenewPresence = useCallback(async () => {
        if (!currentToken) return;
        try {
            const decoded = parseJwt(currentToken);
            const tenantId = decoded ? decoded.tid : null;
            const aadId = decoded ? decoded.oid : null;
            const baseUrl = getApiBaseUrl();
            await fetch(`${baseUrl}/MSTeamsUser/renewPresenceSubscription`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentToken}` },
                body: JSON.stringify({ tenant_id: tenantId, aad_id: aadId })
            });
        } catch (e) {
            console.error("Auto-renew presence subscription failed:", e);
        } finally {
            setSubscriptionRefreshKey(k => k + 1);
        }
    }, [currentToken]);

    const handleToggleChange = (user, newStatus, currentContactId) => {
        // Detect 0→1 transition: trigger auto-renew after batch flush (3s) + buffer
        if (newStatus === "Active") {
            const currentActiveCount = Object.values(userStatuses).filter(s => s === "Active").length;
            if (currentActiveCount === 0) {
                if (autoRenewTimerRef.current) clearTimeout(autoRenewTimerRef.current);
                autoRenewTimerRef.current = setTimeout(() => autoRenewPresence(), 4000);
            }
        }
        handleSaveUserConfig(user, currentContactId, newStatus);
    };

    const handleOpenCreateModal = (user) => {
        setSelectedUserForCreate(user);
        setCreateModalOpen(true);
    };

    const assignedContactIds = useMemo(() => {
        return new Set(Object.values(userMappings).filter(Boolean).map(String));
    }, [userMappings]);

    const isAlreadyCreated = useMemo(() => {
        if (!selectedUserForCreate) return false;
        const targetEmail = (selectedUserForCreate.userPrincipalName || selectedUserForCreate.mail || "").toLowerCase();
        if (!targetEmail) return false;
        return dropdownOptions.some(opt =>
            opt.data && opt.data.email &&
            opt.data.email.toLowerCase() === targetEmail
        );
    }, [selectedUserForCreate, dropdownOptions]);

    // --- Client-side filtering + sort by displayName A-Z (German locale for umlauts) ---
    const filteredUsers = useMemo(() => {
        const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });
        return users
            .filter(user => {
                const aadIdLower = String(user.id).toLowerCase();
                const mappedContactId = userMappings[aadIdLower];
                const isMapped = Boolean(mappedContactId);
                const status = userStatuses[aadIdLower];
                const isSynced = status === "Active";

                if (syncFilter === 'synced' && !isSynced) return false;
                if (syncFilter === 'notSynced' && isSynced) return false;
                if (mappingFilter === 'mapped' && !isMapped) return false;
                if (mappingFilter === 'unmapped' && isMapped) return false;

                return true;
            })
            .sort((a, b) => collator.compare(a.displayName || '', b.displayName || ''));
    }, [users, userMappings, userStatuses, syncFilter, mappingFilter]);

    // --- Client-side pagination ---
    const totalFilteredCount = filteredUsers.length;
    const totalPages = Math.ceil(totalFilteredCount / ITEMS_PER_PAGE);
    const paginatedUsers = useMemo(() => {
        const start = clientPage * ITEMS_PER_PAGE;
        return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredUsers, clientPage]);

    // Reset page when filters change
    useEffect(() => {
        setClientPage(0);
    }, [syncFilter, mappingFilter]);

    // --- Fetch profile photos for visible page only ---
    useEffect(() => {
        if (!currentToken || paginatedUsers.length === 0) return;
        let cancelled = false;

        const fetchPhotos = async () => {
            const missing = paginatedUsers.filter(u => !(u.id in userPhotos));
            if (missing.length === 0) return;

            const newEntries = {};
            await Promise.allSettled(missing.map(async (user) => {
                try {
                    const res = await fetch(
                        `https://graph.microsoft.com/v1.0/users/${user.id}/photo/$value`,
                        { headers: { Authorization: `Bearer ${currentToken}` } }
                    );
                    if (!res.ok) return;
                    const blob = await res.blob();
                    newEntries[user.id] = URL.createObjectURL(blob);
                } catch {
                    // no photo available, skip
                }
            }));
            if (!cancelled && Object.keys(newEntries).length > 0) {
                photoUrlsRef.current = { ...photoUrlsRef.current, ...newEntries };
                setUserPhotos(prev => ({ ...prev, ...newEntries }));
            }
        };

        fetchPhotos();
        return () => { cancelled = true; };
    }, [currentToken, paginatedUsers]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Table definition (3 columns) ---
    const tableHeader = {
        styles: { backgroundColor: 'rgba(98, 100, 167, 0.04)' },
        items: [
            { content: 'Mitarbeiter (Teams)', key: 'header-employee', styles: { flex: 3, minWidth: '280px', fontWeight: 600, color: '#1a1a2e' } },
            { content: (
                    <Flex vAlign="center" gap="gap.smaller">
                        <span>Aktionen</span>
                        <TooltipWrapper content="Steuerung der Präsenz-Synchronisierung. Siehe Legende oberhalb der Tabelle.">
                            <span style={{ fontSize: 13, color: '#6264A7', cursor: 'help' }}>ⓘ</span>
                        </TooltipWrapper>
                    </Flex>
                ), key: 'header-actions', styles: { flex: 1, minWidth: '160px', fontWeight: 600, color: '#1a1a2e' } },
            { content: 'ebuero-Zuordnung', key: 'header-mapping', styles: { flex: 2, minWidth: '260px', fontWeight: 600, color: '#1a1a2e' } },
        ]
    };

    const tableRows = paginatedUsers.map((user) => {
        const aadIdLower = String(user.id).toLowerCase();
        const mappedContactId = userMappings[aadIdLower] || "";
        const isMapped = Boolean(mappedContactId);
        const currentStatus = userStatuses[aadIdLower] || "Inactive";
        const presence = presenceData[aadIdLower];

        return {
            styles: { ':hover': { backgroundColor: 'rgba(98, 100, 167, 0.06)' } },
            key: user.id,
            items: [
                {
                    content: (
                        <Flex gap="gap.small" vAlign="center">
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                <Avatar image={userPhotos[user.id]} name={user.displayName} styles={{ borderRadius: '50%' }} label={{ styles: { backgroundColor: '#6264A7', color: 'white' } }} />
                                {presence && (
                                    <PresenceBadge
                                        availability={presence.availability}
                                        activity={presence.activity}
                                        dotOnly
                                    />
                                )}
                            </div>
                            <Flex column style={{ overflow: 'hidden' }}>
                                <Text content={user.displayName} weight="bold" truncate />
                                <Text content={user.userPrincipalName} size="small" style={{ color: '#605e5c' }} truncate />
                            </Flex>
                        </Flex>
                    ),
                    key: 'employee', styles: { flex: 3, minWidth: '280px', overflow: 'hidden' }
                },
                {
                    content: (
                        <Flex gap="gap.small" vAlign="center">
                            <StatusToggle
                                user={user}
                                contactId={mappedContactId}
                                currentStatus={currentStatus}
                                onToggleChange={handleToggleChange}
                            />
                            <ActionIcons
                                isMapped={isMapped}
                                syncStatus={currentStatus}
                                onLinkClick={() => {}}
                                onCreateClick={() => handleOpenCreateModal(user)}
                                showCreate={!isMapped}
                            />
                        </Flex>
                    ),
                    key: 'actions', styles: { flex: 1, minWidth: '160px' }
                },
                {
                    content: <MappingSelector
                                options={dropdownOptions}
                                loading={configLoading}
                                onUserSelect={(contactId) => handleMappingSelect(user, contactId)}
                                selectedContactId={mappedContactId}
                                assignedContactIds={assignedContactIds}
                             />,
                    key: 'mapping', styles: { flex: 2, minWidth: '260px' }
                },
            ]
        };
    });

    if (authLoading) {
        return (
            <Flex fill column align="center" justify="center" style={{ height: '80vh' }}>
                <Loader label="Authentifizierung..." />
            </Flex>
        );
    }

    return (
        <div style={{ padding: '24px 32px', margin: '0 auto' }}>
            {/* Back button */}
            <Flex hAlign="start" style={{ marginBottom: '8px' }}>
                <Button icon={<ArrowLeftIcon />} content="Einstellungen" text onClick={handleBack} style={{ color: '#6264A7' }} />
            </Flex>

            {/* Page header with action buttons */}
            <Flex space="between" vAlign="center" style={{ marginBottom: '24px' }}>
                <Flex column>
                    <Header as="h2" content={`Mitarbeiter-Synchronisierung (${totalUserCount} gesamt)`} style={{ color: '#1a1a2e', margin: 0, fontWeight: 700, letterSpacing: '-0.3px' }} />
                    <Text content="Mitarbeiterzuordnungen und Präsenz-Sync verwalten" size="small" style={{ color: '#605e5c', marginTop: '2px' }} />
                </Flex>
                <Flex gap="gap.small">
                    <Button
                        content="Mitarbeiter neu laden"
                        size="small"
                        loading={fullSyncLoading}
                        disabled={fullSyncLoading}
                        style={{ backgroundColor: '#6264A7', color: 'white' }}
                        onClick={() => setFullSyncConfirm(true)}
                    />
                    <Button
                        content="Einstellungen"
                        size="small"
                        onClick={handleSettings}
                    />
                </Flex>
            </Flex>

            {/* Presence subscription card */}
            <PresenceSubscriptionCard token={currentToken} refreshKey={subscriptionRefreshKey} />

            {/* Bulk actions */}
            <Flex gap="gap.small" style={{ marginBottom: '12px' }}>
                <Button
                    content="Auto-Map (E-Mail)"
                    size="small"
                    loading={autoMapLoading}
                    disabled={autoMapLoading}
                    onClick={() => setAutoMapConfirm({ open: true, matchBy: 'email' })}
                    style={{ borderColor: '#6264A7', color: '#6264A7' }}
                />
                <Button
                    content="Auto-Map (Name)"
                    size="small"
                    loading={autoMapLoading}
                    disabled={autoMapLoading}
                    onClick={() => setAutoMapConfirm({ open: true, matchBy: 'name' })}
                    style={{ borderColor: '#6264A7', color: '#6264A7' }}
                />
            </Flex>

            {/* Table section */}
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '6px',
                borderLeft: '3px solid #6264A7',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                padding: '20px 24px',
                marginBottom: '20px',
            }}>
                {/* Search + Filters row */}
                <Flex space="between" vAlign="center" style={{ marginBottom: '12px', flexWrap: 'wrap', rowGap: '8px' }}>
                    <Input
                        icon={<SearchIcon />}
                        placeholder="Nach Name oder E-Mail suchen..."
                        clearable
                        value={searchTerm}
                        onChange={(e, data) => setSearchTerm(data.value)}
                        style={{ maxWidth: '300px', backgroundColor: 'rgba(98, 100, 167, 0.04)', border: '1px solid #e1dfdd', borderRadius: '4px' }}
                    />
                    <Flex gap="gap.small">
                        <select
                            value={syncFilter}
                            onChange={(e) => setSyncFilter(e.target.value)}
                            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e1dfdd', fontSize: '13px', backgroundColor: '#fff' }}
                        >
                            {SYNC_FILTER_OPTIONS.map(opt => (
                                <option key={opt.key} value={opt.key}>{opt.header}</option>
                            ))}
                        </select>
                        <select
                            value={mappingFilter}
                            onChange={(e) => setMappingFilter(e.target.value)}
                            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e1dfdd', fontSize: '13px', backgroundColor: '#fff' }}
                        >
                            {MAPPING_FILTER_OPTIONS.map(opt => (
                                <option key={opt.key} value={opt.key}>{opt.header}</option>
                            ))}
                        </select>
                    </Flex>
                </Flex>

                {/* Icon legend */}
                <div style={{ marginBottom: '12px' }}>
                    <IconLegend />
                </div>

                {error ? (
                    <Text error content={`Error: ${error}`} />
                ) : (
                    <>
                        <div style={{ width: '100%', overflowX: 'auto', minHeight: '300px' }}>
                            {loading ? (
                                <Flex fill hAlign="center" vAlign="center">
                                    <Loader label={debouncedSearchTerm ? "Suche..." : "Benutzer werden geladen..."} />
                                </Flex>
                            ) : paginatedUsers.length === 0 ? (
                                <Flex fill hAlign="center" vAlign="center">
                                    <Text content={debouncedSearchTerm ? `Keine Ergebnisse für "${debouncedSearchTerm}"` : "Keine Benutzer gefunden."} style={{ color: '#605e5c' }} />
                                </Flex>
                            ) : (
                                <Table header={tableHeader} rows={tableRows} aria-label="Mitarbeiter-Synchronisierung" />
                            )}
                        </div>

                        {/* Pagination */}
                        <Flex space="between" vAlign="center" style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #edebe9' }}>
                            <Text size="small" style={{ color: '#605e5c' }} content={`${paginatedUsers.length} von ${totalFilteredCount} Mitarbeitern angezeigt (Seite ${clientPage + 1} von ${totalPages || 1})`} />
                            <Flex gap="gap.small">
                                <Button
                                    icon={<ChevronStartIcon />}
                                    content="Zurück"
                                    size="small"
                                    disabled={clientPage === 0 || loading}
                                    onClick={() => setClientPage(prev => Math.max(0, prev - 1))}
                                    style={{ backgroundColor: clientPage > 0 && !loading ? '#6264A7' : undefined, color: clientPage > 0 && !loading ? 'white' : undefined }}
                                />
                                <Button
                                    icon={<ChevronEndIcon />}
                                    content="Weiter"
                                    iconPosition="after"
                                    size="small"
                                    disabled={clientPage >= totalPages - 1 || loading}
                                    onClick={() => setClientPage(prev => Math.min(totalPages - 1, prev + 1))}
                                    style={{ backgroundColor: clientPage < totalPages - 1 && !loading ? '#6264A7' : undefined, color: clientPage < totalPages - 1 && !loading ? 'white' : undefined }}
                                />
                                {/* Graph next page (load more from server) */}
                                {nextLink && clientPage >= totalPages - 1 && (
                                    <Button
                                        content="Weitere laden..."
                                        size="small"
                                        disabled={loading}
                                        onClick={handleNextPage}
                                        style={{ color: '#6264A7' }}
                                        text
                                    />
                                )}
                            </Flex>
                        </Flex>
                    </>
                )}
            </div>

            {/* Toast notification */}
            {toast.visible && (
                <div style={{
                    position: 'fixed', bottom: '24px', left: '50%',
                    transform: 'translateX(-50%)', zIndex: 100001,
                    minWidth: '300px', maxWidth: '500px'
                }}>
                    <Alert
                        content={toast.message}
                        success={toast.type === 'success'}
                        danger={toast.type === 'error'}
                        dismissible
                        onVisibleChange={(e, { visible }) => {
                            if (!visible) setToast(prev => ({ ...prev, visible: false }));
                        }}
                    />
                </div>
            )}

            {/* Full Sync confirmation dialog */}
            <Dialog
                open={fullSyncConfirm}
                onConfirm={() => {
                    setFullSyncConfirm(false);
                    handleFullSync();
                }}
                onCancel={() => setFullSyncConfirm(false)}
                confirmButton="Ja, ausführen"
                cancelButton="Abbrechen"
                header="Mitarbeiter neu laden"
                content="Alle Mitarbeiter werden aus Azure AD neu geladen. Bestehende Zuordnungen und Präsenzdaten bleiben erhalten. Dieser Vorgang kann bei großen Mandanten einige Minuten dauern."
            />

            {/* Auto-Map confirmation dialog */}
            <Dialog
                open={autoMapConfirm.open}
                onConfirm={() => {
                    const { matchBy } = autoMapConfirm;
                    setAutoMapConfirm({ open: false, matchBy: null });
                    handleAutoMap(matchBy);
                }}
                onCancel={() => setAutoMapConfirm({ open: false, matchBy: null })}
                confirmButton="Ja, ausführen"
                cancelButton="Abbrechen"
                header="Auto-Map bestätigen"
                content={
                    autoMapConfirm.matchBy === 'email'
                        ? 'Alle Mitarbeiter automatisch per E-Mail-Adresse zuordnen? Bestehende Zuordnungen bleiben erhalten.'
                        : 'Alle Mitarbeiter automatisch per Name zuordnen? Bestehende Zuordnungen bleiben erhalten.'
                }
            />

            {/* Create employee modal */}
            {createModalOpen && selectedUserForCreate && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 100000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: '#fff', padding: '24px', borderRadius: '8px',
                        minWidth: '420px', maxWidth: '480px',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                        borderTop: '3px solid #6264A7',
                        display: 'flex', flexDirection: 'column'
                    }}>
                        <Flex column gap="gap.medium">
                            <Flex hAlign="center">
                                <Text content="Bestätigung" size="large" weight="bold" />
                            </Flex>
                            <Divider />
                            <Flex column gap="gap.small">
                                <Text content={`Name: ${selectedUserForCreate.displayName}`} weight="semibold" />
                                <Text content={`E-Mail: ${selectedUserForCreate.userPrincipalName}`} />
                            </Flex>
                            <Flex hAlign="end" gap="gap.small" style={{ marginTop: '20px' }}>
                                <Button content="Abbrechen" onClick={handleCancelCreate} />
                                <Button
                                    content={isAlreadyCreated ? "Bereits vorhanden" : "Ok"}
                                    primary
                                    disabled={isAlreadyCreated}
                                    style={{ backgroundColor: isAlreadyCreated ? '#8f91c7' : '#6264A7', color: 'white' }}
                                    onClick={handleConfirmCreateEmployee}
                                />
                            </Flex>
                        </Flex>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersConfig;
